import type { TeactNode } from '../../lib/teact/teact';
import React, { memo, useEffect, useRef, useState } from '../../lib/teact/teact';
import { getActions, getGlobal, withGlobal } from '../../global';

import type { MigrationErrorPresentation } from '../../global/types';

import {
  APP_NAME,
  AUTO_CONFIRM_DURATION_MINUTES,
  IS_LEGENDS_WALLET,
  PIN_LENGTH,
  SUPPORT_USERNAME,
  WRONG_ATTEMPTS_BEFORE_LOG_OUT_SUGGESTION,
} from '../../config';
import {
  selectHasLegacyBiometrics,
  selectIsBiometricAuthEnabled,
  selectLegacyAuthConfig,
  selectShouldMigrate,
} from '../../global/selectors';
import { selectAuthUsageCountRequest, selectEnclaveToken } from '../../global/selectors/enclave';
import {
  getDoesUsePinPad,
  getIsBiometricAuthSupported,
  getIsFaceIdAvailable,
  getIsTouchIdAvailable,
} from '../../util/biometrics';
import buildClassName from '../../util/buildClassName';
import captureKeyboardListeners from '../../util/captureKeyboardListeners';
import { stopEvent } from '../../util/domEvents';
import { getTranslation } from '../../util/langProvider';
import { logDebug, logDebugError } from '../../util/logs';
import { toNativeDigits } from '../../util/nativeDigits';
import { pause } from '../../util/schedulers';
import { createSignal } from '../../util/signals';
import {
  getIsTelegramBiometricTokenMissing,
  getTelegramBiometricStateRevision,
  refreshTelegramBiometricState,
} from '../../util/telegram';
import { enclave, type LegacyAuthConfig } from '../../enclave';
import { ANIMATED_STICKERS_PATHS } from './helpers/animatedAssets';

import useBackgroundMode, { isBackgroundModeActive } from '../../hooks/useBackgroundMode';
import { useDeviceScreen } from '../../hooks/useDeviceScreen';
import useEffectOnce from '../../hooks/useEffectOnce';
import useFlag from '../../hooks/useFlag';
import useFocusAfterAnimation from '../../hooks/useFocusAfterAnimation';
import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import { useMatchCount } from '../../hooks/useMatchCount';
import useTimeout from '../../hooks/useTimeout';
import useToggleClass from '../../hooks/useToggleClass';

import LogOutModal from '../main/modals/LogOutModal';
import AnimatedIconWithPreview from './AnimatedIconWithPreview';
import Button from './Button';
import Checkbox from './Checkbox';
import Input from './Input';
import PasscodeHeader from './PasscodeHeader';
import PinPad from './PinPad';

import modalStyles from './Modal.module.scss';
import styles from './PasswordForm.module.scss';

type OperationType = 'transfer' | 'sending' | 'staking' | 'unstaking' | 'swap'
  | 'unfreeze' | 'passcode' | 'unlock' | 'claim' | 'turnOnBiometrics' | 'mintCard';

interface OwnProps {
  isActive: boolean;
  isLoading?: boolean;
  operationType?: OperationType;
  cancelLabel?: string;
  submitLabel?: string | TeactNode[];
  stickerSize?: number;
  placeholder?: string;
  error?: string;
  pinPadHeading?: string;
  pinPadTitle?: string;
  help?: string;
  resetStateDelayMs?: number;
  containerClassName?: string;
  pinPadClassName?: string;
  withCloseButton?: boolean;
  isFullWidthButton?: boolean;
  children?: TeactNode;
  noAnimatedIcon?: boolean;
  inputWrapperClassName?: string;
  forceBiometricsInMain?: boolean;
  noBiometrics?: boolean;
  errorClassName?: string;
  noAutoConfirm?: boolean;
  /**
   * Secret reads this flow needs beyond the single one every operation gets. A usage is spent when
   * the private key is read, not when an API call is made, so only a flow that reads the key in more
   * than one step has to ask for more; the session dies on the last read it granted. A flow that has
   * to name the largest count it might need gives back what it did not spend when it ends.
   */
  extraAuthUsages?: number;
  onCancel?: NoneToVoidFunction;
  onUpdate?: NoneToVoidFunction;
  onAuthorize: (enclaveToken: string) => void;
  onError?: (error?: string) => void;
}

interface StateProps {
  isPasswordNumeric?: boolean;
  isAutoConfirmEnabled?: boolean;
  enclaveSessionValidUntil?: number;
  isBiometricAuthEnabled: boolean;
  hasPasscodeAuth?: boolean;
  shouldMigrate?: boolean;
  hasLegacyBiometrics?: boolean;
  legacyAuthConfig?: LegacyAuthConfig;
  authUsageCountRequest?: number;
}

const STICKER_SIZE = 180;
const LEGENDS_PIN_STICKER_SIZE_PX = 96;
const APPEAR_ANIMATION_DURATION_MS = 300;

const [getHandleBiometricsSignal, setHandleBiometricsSignal] = createSignal(Date.now());

export function triggerPasswordFormHandleBiometrics(e?: MouseEvent | KeyboardEvent) {
  if (e) {
    stopEvent(e);
  }
  setHandleBiometricsSignal(Date.now());
}

function useMigrationFailureDialog(operationType?: OperationType) {
  const { showDialog } = getActions();

  return useLastCallback((titleKey: string, messageKey: string, errorCode?: string) => {
    showDialog({
      title: titleKey,
      message: getTranslation(messageKey, {
        support_link: (
          <a href={`https://t.me/${SUPPORT_USERNAME}`} target="_blank" rel="noreferrer">
            @{SUPPORT_USERNAME}
          </a>
        ),
        error_code: errorCode,
      }),
      noBackdropClose: true,
      isInAppLock: operationType === 'unlock',
    });
  });
}

export function PasswordForm({
  isActive,
  isLoading,
  operationType,
  isPasswordNumeric,
  isBiometricAuthEnabled: isBiometricAuthEnabledProp,
  cancelLabel,
  submitLabel,
  stickerSize = STICKER_SIZE,
  placeholder = getDoesUsePinPad() ? 'Enter your passcode' : 'Enter your password',
  error,
  pinPadHeading,
  pinPadTitle,
  help,
  resetStateDelayMs,
  containerClassName,
  pinPadClassName,
  children,
  withCloseButton,
  isFullWidthButton,
  noAnimatedIcon,
  inputWrapperClassName,
  forceBiometricsInMain,
  noBiometrics,
  errorClassName,
  isAutoConfirmEnabled,
  enclaveSessionValidUntil,
  noAutoConfirm,
  shouldMigrate,
  hasPasscodeAuth,
  hasLegacyBiometrics,
  legacyAuthConfig,
  authUsageCountRequest,
  extraAuthUsages,
  onUpdate,
  onCancel,
  onAuthorize,
  onError,
}: OwnProps & StateProps) {
  const {
    setIsAutoConfirmEnabled,
    setEnclaveSession,
    enableBiometrics,
    migrateLegacyAuth,
    migrateLegacyBiometricAuth,
    upgradeMultichainAccounts,
  } = getActions();

  const lang = useLang();

  const isBiometricAuthEnabled = isBiometricAuthEnabledProp && !noBiometrics && getIsBiometricAuthSupported();

  const inputRef = useRef<HTMLInputElement>();
  const [inputValue, setInputValue] = useState<string>('');
  const [localError, setLocalError] = useState<string>('');
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isLegacyPasswordMode, setIsLegacyPasswordMode] = useState(false);
  const [isBiometricFallbackForced, setIsBiometricFallbackForced] = useState(false);
  const [biometricStateRevision, setBiometricStateRevision] = useState(getTelegramBiometricStateRevision());
  const { isSmallHeight, isPortrait } = useDeviceScreen();
  const doesUsePinPad = getDoesUsePinPad();
  const canAttemptBiometrics = isBiometricAuthEnabled && !isBiometricFallbackForced;
  const withAutoConfirm = useCanAutoConfirm(enclaveSessionValidUntil, noAutoConfirm || doesUsePinPad);
  const canArmAutoConfirm = !doesUsePinPad
    && !isBiometricAuthEnabledProp
    && operationType !== 'turnOnBiometrics';
  const isLongSession = canArmAutoConfirm && Boolean(isAutoConfirmEnabled);
  const isSubmitDisabled = !inputValue.length && !withAutoConfirm;
  const canUsePinPad = doesUsePinPad
    && !isLegacyPasswordMode
    && (!isBiometricAuthEnabled || hasPasscodeAuth);
  const canUseLegacyPassword = IS_LEGENDS_WALLET && !isPasswordNumeric;
  const [isLogOutModalOpened, openLogOutModal, closeLogOutModal] = useFlag(false);
  // The biometric screen offers its retry only while something failed, and the dialog paths clear the
  // inline error the retry used to be keyed on, which would leave that screen without a single control
  const [hasMigrationFailed, markMigrationFailed, clearMigrationFailure] = useFlag(false);
  const shouldSuggestLogout = useMatchCount(!!error || !!localError, WRONG_ATTEMPTS_BEFORE_LOG_OUT_SUGGESTION);
  const showMigrationFailureDialog = useMigrationFailureDialog(operationType);
  const isAuthorizingRef = useRef(false);
  const isAutomaticBiometricAttemptScheduledRef = useRef(false);
  const shouldSkipNextBiometricForegroundRef = useRef(false);
  const isBusy = Boolean(isLoading || isAuthorizing);
  const isPasscodeScreen = IS_LEGENDS_WALLET;

  /**
   * Both migration entry points report through here, so a diagnosis that needs the dialog cannot end
   * up in the inline error slot on one path while getting the dialog on the other. The inline slot is
   * a single line under the input, which would leave the "do not reinstall" part of the answer unsaid.
   */
  const handleMigrationError = useLastCallback((presentation: MigrationErrorPresentation) => {
    isAuthorizingRef.current = false;
    setIsAuthorizing(false);

    if (presentation.kind === 'inline') {
      setLocalError(presentation.text);
      onError?.(presentation.text);
      return;
    }

    // An earlier attempt may have left "Wrong password" under the input, and leaving it there next to
    // an answer that says the password is not the problem contradicts what is being said. It is
    // cleared on both sides, because the parent owns a slot of its own that outranks the local one.
    setLocalError('');
    onError?.(undefined);
    markMigrationFailed();

    // A dismissed prompt is not a failure to report. The mark above is what puts the retry control
    // back on the biometric screen, which is all this path owes the user.
    if (presentation.kind === 'silent') return;

    // The pinpad holds its digits until the value is cleared, so a full pinpad behind the dialog
    // reads as a frozen screen
    setInputValue('');
    showMigrationFailureDialog(presentation.titleKey, presentation.messageKey, presentation.errorCode);
  });

  // Legends uses the same unlock to establish fee-balance access in the background.
  const prepaidAccessUsageCount = IS_LEGENDS_WALLET ? 1 : 0;
  const operationUsageCount = 1 + prepaidAccessUsageCount + (extraAuthUsages ?? 0);
  const extraUsages = prepaidAccessUsageCount + (authUsageCountRequest ?? 0) + (extraAuthUsages ?? 0);
  const usageCount = extraUsages ? 1 + extraUsages : undefined;

  const handleAuthorized = useLastCallback((enclaveToken: string) => {
    // The handler is a no-op when there is nothing to upgrade
    upgradeMultichainAccounts({ enclaveToken });
    onAuthorize(enclaveToken);
    setIsAuthorizing(false);
  });

  useEffect(() => {
    if (isActive) {
      setLocalError('');
      setInputValue('');
      setIsLegacyPasswordMode(false);
      setIsBiometricFallbackForced(getIsTelegramBiometricTokenMissing());
      clearMigrationFailure();
      isAuthorizingRef.current = false;
      setIsAuthorizing(false);
    }
  }, [isActive]);

  useEffect(() => {
    if (error && !isLoading) {
      isAuthorizingRef.current = false;
      setIsAuthorizing(false);
    }
  }, [error, isLoading]);

  const handleSubmit = useLastCallback(async (pin?: string) => {
    // Prevent double authorization (e.g., race between PIN input and biometrics)
    if (isAuthorizingRef.current || isLoading) return;
    isAuthorizingRef.current = true;
    setIsAuthorizing(true);

    if (withAutoConfirm) {
      handleAuthorized(selectEnclaveToken(getGlobal())!);
      return;
    }

    const password = pin ?? inputValue;
    const shouldRepairTelegramBiometrics = isBiometricAuthEnabled
      && Boolean(hasPasscodeAuth)
      && getIsTelegramBiometricTokenMissing();

    if (isBiometricAuthEnabled && hasPasscodeAuth) {
      logDebug('[PasswordForm][biometrics] passcode fallback selected', { operationType });
    }

    // Migration from legacy auth to Enclave
    if (shouldMigrate) {
      migrateLegacyAuth({
        password,
        isLongSession,
        // A migration hands its session straight to the operation and never starts the multichain
        // upgrade, so budgeting for the upgrade would leave those reads unspent - and unspent means
        // available for good, since a counted session has no expiry. Turning biometrics back on does
        // read the key, and that read comes out of this same session.
        usageCount: operationUsageCount + (hasLegacyBiometrics ? 1 : 0),
        onSuccess: (token) => {
          if (hasLegacyBiometrics) {
            enableBiometrics({});
          }
          onAuthorize(token);
          setIsAuthorizing(false);
        },
        onError: handleMigrationError,
      });
      return;
    }

    // Normal authorization
    logDebug('[PasswordForm][passcode] authorization started', {
      operationType,
      isLegacyPasswordMode,
      shouldRepairTelegramBiometrics,
    });

    let enclaveSession = await enclave.authorize(
      'passcode',
      isLongSession,
      password,
      shouldRepairTelegramBiometrics ? 1 : usageCount,
    );

    if (!enclaveSession) {
      isAuthorizingRef.current = false;
      setIsAuthorizing(false);
      logDebugError('[PasswordForm][passcode] authorization rejected', {
        operationType,
        isLegacyPasswordMode,
      });
      const errorMessage = 'Wrong password, please try again.';
      setLocalError(errorMessage);
      onError?.(errorMessage);
      return;
    }

    if (shouldRepairTelegramBiometrics) {
      try {
        logDebug('[PasswordForm][biometrics] token repair started', { operationType });
        const repairedSession = await enclave.migrateAuth(
          enclaveSession.token,
          'biometric',
          undefined,
          false,
          usageCount,
        );
        if (!repairedSession) {
          throw new Error('Failed to restore Telegram biometric credentials');
        }

        enclaveSession = repairedSession;
        logDebug('[PasswordForm][biometrics] token repair succeeded', { operationType });
      } catch (err) {
        logDebugError('[PasswordForm][biometrics] token repair failed', {
          operationType,
          error: err,
        });
        enclaveSession = await enclave.authorize('passcode', isLongSession, password, usageCount);
        if (!enclaveSession) {
          isAuthorizingRef.current = false;
          setIsAuthorizing(false);
          const errorMessage = 'Something went wrong.';
          setLocalError(errorMessage);
          onError?.(errorMessage);
          return;
        }
      }
    }

    setEnclaveSession(enclaveSession);
    logDebug('[PasswordForm][passcode] authorization succeeded', {
      operationType,
      repairedBiometrics: shouldRepairTelegramBiometrics,
    });
    handleAuthorized(enclaveSession.token);
  });

  const handleBiometrics = useLastCallback(async () => {
    // Prevent double authorization (e.g., race between PIN input and biometrics)
    if (isAuthorizingRef.current || isLoading) return;
    isAuthorizingRef.current = true;
    setIsAuthorizing(true);

    try {
      setLocalError('');
      if (getIsTelegramBiometricTokenMissing()) {
        isAuthorizingRef.current = false;
        setIsAuthorizing(false);
        setIsBiometricFallbackForced(true);
        logDebug('[PasswordForm][biometrics] missing token fallback selected', {
          operationType,
          hasPasscodeFallback: Boolean(hasPasscodeAuth),
        });
        return;
      }

      logDebug('[PasswordForm][biometrics] authorization started', {
        operationType,
        hasPasscodeFallback: Boolean(hasPasscodeAuth),
        usageCount,
      });

      const enclaveSession = await enclave.authorizeOrThrow('biometric', false, undefined, usageCount);
      if (!enclaveSession) {
        isAuthorizingRef.current = false;
        setIsAuthorizing(false);
        const errorMessage = 'Biometric confirmation failed';
        setLocalError(errorMessage);
        onError?.(errorMessage);
        return;
      }

      setEnclaveSession(enclaveSession);
      logDebug('[PasswordForm][biometrics] authorization succeeded', { operationType });
      handleAuthorized(enclaveSession.token);
    } catch (err: any) {
      isAuthorizingRef.current = false;
      setIsAuthorizing(false);
      logDebugError('[PasswordForm][biometrics] authorization failed', {
        operationType,
        hasPasscodeFallback: Boolean(hasPasscodeAuth),
        error: err,
      });
      const errorMessage = 'Biometric confirmation failed';
      setLocalError(errorMessage);
      onError?.(errorMessage);
    }
  });

  // Handle legacy biometrics migration
  const handleLegacyBiometricsMigration = useLastCallback(() => {
    if (!legacyAuthConfig || legacyAuthConfig.kind === 'password') {
      return;
    }

    // Prevent double authorization
    if (isAuthorizingRef.current || isLoading) return;
    isAuthorizingRef.current = true;
    setIsAuthorizing(true);

    setLocalError('');

    migrateLegacyBiometricAuth({
      legacyAuthConfig,
      isLongSession,
      // The upgrade is left out for the same reason as in the passcode migration above
      usageCount: operationUsageCount,
      onSuccess: (token) => {
        onAuthorize(token);
        setIsAuthorizing(false);
      },
      onError: handleMigrationError,
    });
  });

  const handleAutomaticBiometrics = useLastCallback(async () => {
    if (
      isAutomaticBiometricAttemptScheduledRef.current
      || !isActive
      || !isBiometricAuthEnabledProp
      || noBiometrics
      || isBiometricFallbackForced
      || withAutoConfirm
      || isBackgroundModeActive()
    ) {
      return;
    }

    isAutomaticBiometricAttemptScheduledRef.current = true;
    await pause(APPEAR_ANIMATION_DURATION_MS);
    isAutomaticBiometricAttemptScheduledRef.current = false;

    if (
      !isActive
      || !isBiometricAuthEnabledProp
      || noBiometrics
      || isBiometricFallbackForced
      || withAutoConfirm
      || !getIsBiometricAuthSupported()
      || isBackgroundModeActive()
    ) {
      return;
    }

    if (shouldMigrate && hasLegacyBiometrics && legacyAuthConfig) {
      handleLegacyBiometricsMigration();
    } else if (!shouldMigrate) {
      void handleBiometrics();
    }
  });

  const handleBiometricBackground = useLastCallback(() => {
    if (isAuthorizingRef.current) {
      shouldSkipNextBiometricForegroundRef.current = true;
    }
  });

  const handleBiometricForeground = useLastCallback(() => {
    if (shouldSkipNextBiometricForegroundRef.current) {
      shouldSkipNextBiometricForegroundRef.current = false;
      return;
    }

    refreshTelegramBiometricState();
    void handleAutomaticBiometrics();
  });

  useBackgroundMode(handleBiometricBackground, handleBiometricForeground, !isActive);

  useEffectOnce(() => {
    return getTelegramBiometricStateRevision.subscribe(() => {
      setBiometricStateRevision(getTelegramBiometricStateRevision());
    });
  });

  useEffect(() => {
    void handleAutomaticBiometrics();
  }, [
    biometricStateRevision, forceBiometricsInMain, handleAutomaticBiometrics, isActive,
    canAttemptBiometrics, withAutoConfirm, shouldMigrate, hasLegacyBiometrics, legacyAuthConfig,
  ]);

  useEffectOnce(() => {
    // When signal is triggered, use legacy or new biometrics based on migration state
    return getHandleBiometricsSignal.subscribe(() => {
      if (shouldMigrate && hasLegacyBiometrics && legacyAuthConfig) {
        void handleLegacyBiometricsMigration();
      } else if (!shouldMigrate) {
        void handleBiometrics();
      }
    });
  });

  useFocusAfterAnimation(inputRef, !isActive || (canAttemptBiometrics && !isLegacyPasswordMode));

  useToggleClass({ className: 'is-password-form-visible', isActive });

  const handleClearError = useLastCallback(() => {
    setLocalError('');
    clearMigrationFailure();
    onUpdate?.();
    onError?.(undefined);
  });

  const handleInput = useLastCallback((value: string) => {
    setInputValue(value);
    handleClearError();
  });

  const handleUseLegacyPassword = useLastCallback(() => {
    setInputValue('');
    setLocalError('');
    setIsLegacyPasswordMode(true);
  });

  const handleAutoConfirmChange = useLastCallback((isEnabled: boolean) => {
    setIsAutoConfirmEnabled({ isEnabled });
  });

  const handleOpenLogOutModal = useLastCallback((e: React.MouseEvent) => {
    stopEvent(e);
    openLogOutModal();
  });

  useEffect(() => {
    return isSubmitDisabled || isBusy
      ? undefined
      : captureKeyboardListeners({ onEnter: () => handleSubmit() });
  }, [handleSubmit, isBusy, isSubmitDisabled]);

  function getPinPadTitle() {
    switch (operationType) {
      case 'unfreeze':
        return 'Confirm Unfreezing';
      case 'passcode':
      case 'turnOnBiometrics':
        return 'Confirm Passcode';
      case 'transfer':
      case 'sending':
        return 'Confirm Sending';
      case 'staking':
        return 'Confirm Staking';
      case 'unstaking':
        return 'Confirm Unstaking';
      case 'swap':
        return 'Confirm Swap';
      case 'unlock':
        return undefined;
      case 'claim':
        return 'Confirm Rewards Claim';
      case 'mintCard':
        return 'Confirm Upgrading';
      default:
        return 'Confirm Action';
    }
  }

  const shouldRenderFullWidthButton = operationType === 'unlock';
  const footerButtonsClassName = buildClassName(
    modalStyles.footerButtons,
    shouldRenderFullWidthButton && modalStyles.footerButtonFullWidth,
  );

  function renderFooterButtons() {
    return (
      <div className={footerButtonsClassName}>
        {onCancel && (
          <Button
            isLoading={isBusy && isBiometricAuthEnabled}
            isDisabled={isBusy && !isBiometricAuthEnabled}
            className={modalStyles.buttonHalfWidth}
            onClick={onCancel}
          >
            {cancelLabel || lang('Cancel')}
          </Button>
        )}
        {canAttemptBiometrics && !isLegacyPasswordMode && (Boolean(localError) || hasMigrationFailed) && (
          <Button
            isPrimary
            isLoading={isBusy}
            isDisabled={isBusy}
            className={modalStyles.buttonHalfWidth}
            onClick={!isBusy
              ? (shouldMigrate && hasLegacyBiometrics ? handleLegacyBiometricsMigration : handleBiometrics)
              : undefined}
            shouldStopPropagation
          >
            {lang('Try Again')}
          </Button>
        )}
        {(!canAttemptBiometrics || isLegacyPasswordMode || withAutoConfirm) && (
          <Button
            isPrimary
            isLoading={isBusy}
            isDisabled={isSubmitDisabled}
            className={(shouldRenderFullWidthButton || isFullWidthButton)
              ? modalStyles.buttonFullWidth : modalStyles.buttonHalfWidth}
            onClick={!isBusy ? handleSubmit : undefined}
          >
            {submitLabel || lang('Send')}
          </Button>
        )}
      </div>
    );
  }

  function renderAutoConfirmCheckbox() {
    return (
      <Checkbox
        checked={!!isAutoConfirmEnabled}
        onChange={handleAutoConfirmChange}
        className={styles.autoConfirmCheckbox}
      >
        {toNativeDigits(lang('Remember for %1$d minutes', AUTO_CONFIRM_DURATION_MINUTES) as string)}
      </Checkbox>
    );
  }

  const shouldRenderAutoConfirmCheckbox = canArmAutoConfirm;

  if (canUsePinPad) {
    const hasError = Boolean(localError || error);
    const title = pinPadHeading || getPinPadTitle();
    const actionName = lang(
      !canAttemptBiometrics
        ? 'Enter code'
        : getIsFaceIdAvailable()
          ? 'Enter code or use Face ID'
          : getIsTouchIdAvailable()
            ? 'Enter code or use Touch ID'
            : 'Enter code or use biometrics',
    );
    const translatedTitle = title ? lang(title) : undefined;
    const pinPadLabel = lang(hasError
      ? (localError || error!)
      : isBusy
        ? 'Please wait...'
        : (pinPadTitle || (!isPasscodeScreen && isSmallHeight && title ? title : actionName)));
    const shouldHidePinPadLabel = !hasError && !isSmallHeight && translatedTitle === pinPadLabel;

    const content = (
      <>
        {withCloseButton && (
          <Button
            isRound
            className={buildClassName(modalStyles.closeButton, styles.closeButton)}
            ariaLabel={lang('Close')}
            onClick={onCancel}
          >
            <i className={buildClassName(modalStyles.closeIcon, 'icon-close')} aria-hidden />
          </Button>
        )}
        <div className={buildClassName(
          styles.pinPadHeader,
          IS_LEGENDS_WALLET && styles.pinPadHeaderLegends,
          isPasscodeScreen && styles.passcodeHeader,
          isPasscodeScreen && operationType !== 'unlock' && styles.passcodeConfirmation,
        )}
        >
          {isPasscodeScreen && (
            <PasscodeHeader
              title={translatedTitle || APP_NAME}
              subtitle={operationType === 'unlock' ? lang('Unlock your wallet to continue') : undefined}
            />
          )}
          {!isPasscodeScreen && (isPortrait || IS_LEGENDS_WALLET) && !noAnimatedIcon && (
            <AnimatedIconWithPreview
              play={isActive}
              tgsUrl={ANIMATED_STICKERS_PATHS.guard}
              previewUrl={ANIMATED_STICKERS_PATHS.guardPreview}
              size={IS_LEGENDS_WALLET ? LEGENDS_PIN_STICKER_SIZE_PX : undefined}
              noLoop={false}
              nonInteractive
            />
          )}
          {!isPasscodeScreen && !isSmallHeight && translatedTitle && (
            <div className={styles.title}>{translatedTitle}</div>
          )}
          {(!isPasscodeScreen || operationType !== 'unlock') && children}
        </div>

        {withAutoConfirm ? renderFooterButtons() : (
          <PinPad
            isActive={isActive && !isLogOutModalOpened}
            isPasscodeScreen={isPasscodeScreen}
            isInAppLock={operationType === 'unlock'}
            isLoading={isBusy}
            title={shouldHidePinPadLabel ? '' : pinPadLabel}
            type={hasError ? 'error' : undefined}
            length={PIN_LENGTH}
            resetStateDelayMs={resetStateDelayMs}
            value={inputValue}
            topContent={shouldRenderAutoConfirmCheckbox ? renderAutoConfirmCheckbox() : undefined}
            footer={(
              <>
                {canUseLegacyPassword && (
                  <Button
                    isSimple
                    isText
                    className={styles.legacyPasswordButton}
                    onClick={handleUseLegacyPassword}
                  >
                    {lang('Enter your password')}
                  </Button>
                )}
                {isPasscodeScreen && operationType !== 'unlock' && !withCloseButton && onCancel && (
                  <Button isSimple isText isDisabled={isBusy} className={styles.passcodeCancel} onClick={onCancel}>
                    {cancelLabel || lang('Cancel')}
                  </Button>
                )}
              </>
            )}
            className={buildClassName(
              pinPadClassName,
              isPasscodeScreen && operationType !== 'unlock' && styles.passcodePadCompact,
            )}
            onBiometricsClick={canAttemptBiometrics
              ? (shouldMigrate && hasLegacyBiometrics ? handleLegacyBiometricsMigration : handleBiometrics)
              : undefined}
            onLogOutClick={operationType === 'unlock' ? openLogOutModal : undefined}
            onChange={setInputValue}
            onClearError={handleClearError}
            onSubmit={handleSubmit}
          />
        )}
        {operationType === 'unlock' && (
          <LogOutModal isOpen={isLogOutModalOpened} onClose={closeLogOutModal} isInAppLock />
        )}
      </>
    );

    return withAutoConfirm ? (
      <div className={modalStyles.transitionContent}>
        {content}
      </div>
    ) : content;
  }

  function renderBiometricPrompt() {
    const renderingError = localError || error;
    if (renderingError) {
      return (
        <div className={styles.error}>{lang(renderingError)}</div>
      );
    }

    return (
      <div className={styles.verify}>
        {lang(operationType === 'transfer'
          ? 'Please confirm transfer using biometrics' : 'Please confirm action using biometrics')}
      </div>
    );
  }

  function renderPasswordForm() {
    return (
      <>
        <Input
          ref={inputRef}
          type="password"
          isRequired
          id="first-password"
          wrapperClassName={inputWrapperClassName}
          errorClassName={errorClassName}
          inputMode={isPasswordNumeric ? 'numeric' : undefined}
          error={error ? lang(error) : localError}
          placeholder={lang(placeholder)}
          value={inputValue}
          onInput={handleInput}
          maxLength={isPasswordNumeric ? PIN_LENGTH : undefined}
        />
        {help && !error && (
          <div className={styles.label}>{help}</div>
        )}
        {shouldRenderAutoConfirmCheckbox && renderAutoConfirmCheckbox()}
      </>
    );
  }

  return (
    <div className={buildClassName(
      modalStyles.transitionContent,
      IS_LEGENDS_WALLET && 'rr-block',
      containerClassName,
      isPasscodeScreen && styles.passcodePassword,
      isPasscodeScreen && operationType !== 'unlock' && styles.passcodeConfirmation,
    )}
    >
      {isPasscodeScreen && (
        <PasscodeHeader
          title={lang(pinPadHeading || getPinPadTitle() || APP_NAME)}
          subtitle={operationType === 'unlock' ? lang('Unlock your wallet to continue') : undefined}
        />
      )}
      {!isPasscodeScreen && !noAnimatedIcon && (
        <AnimatedIconWithPreview
          tgsUrl={ANIMATED_STICKERS_PATHS.holdTon}
          previewUrl={ANIMATED_STICKERS_PATHS.holdTonPreview}
          play={isActive}
          size={stickerSize}
          nonInteractive
          noLoop={false}
          className={styles.sticker}
        />
      )}

      {(!isPasscodeScreen || operationType !== 'unlock') && children}

      {!withAutoConfirm && (canAttemptBiometrics && !isLegacyPasswordMode
        ? renderBiometricPrompt()
        : renderPasswordForm())}

      {operationType === 'unlock' && (
        <div className={buildClassName(styles.logOutWrapper, !shouldSuggestLogout && styles.logOutWrapperHidden)}>
          {lang('Can\'t confirm?')}
          <span
            role="button"
            tabIndex={0}
            className={styles.logOutButton}
            onClick={handleOpenLogOutModal}
          >
            {lang('Exit all wallets')}
            <i className={buildClassName('icon-chevron-right', styles.detailsIcon)} aria-hidden />
          </span>
        </div>
      )}

      {renderFooterButtons()}

      {operationType === 'unlock' && (
        <LogOutModal isOpen={isLogOutModalOpened} onClose={closeLogOutModal} isInAppLock />
      )}
    </div>
  );
}

function useCanAutoConfirm(enclaveSessionValidUntil?: number, isDisabled?: boolean) {
  if (isDisabled) return false;

  const autoConfirmTtl = enclaveSessionValidUntil ? enclaveSessionValidUntil - Date.now() : undefined;
  const [canAutoConfirm, setCanAutoConfirm] = useState(
    autoConfirmTtl !== undefined && autoConfirmTtl > 0,
  );

  useEffect(() => {
    const currentTtl = enclaveSessionValidUntil ? enclaveSessionValidUntil - Date.now() : undefined;
    const isValid = currentTtl !== undefined && currentTtl > 0;
    setCanAutoConfirm(isValid);
  }, [enclaveSessionValidUntil]);

  useTimeout(() => {
    setCanAutoConfirm(false);
  }, autoConfirmTtl && autoConfirmTtl > 0 ? autoConfirmTtl : undefined);

  return canAutoConfirm;
}

export default memo(withGlobal<OwnProps>((global): StateProps => {
  const { isPasswordNumeric, isAutoConfirmEnabled } = global.settings;
  const enclaveSessionValidUntil = global.enclaveSession?.validUntil;
  const isBiometricAuthEnabled = selectIsBiometricAuthEnabled(global);
  const shouldMigrate = selectShouldMigrate(global);
  const hasLegacyBiometrics = shouldMigrate ? selectHasLegacyBiometrics(global) : undefined;
  const legacyAuthConfig = shouldMigrate ? selectLegacyAuthConfig(global) : undefined;

  return {
    isPasswordNumeric,
    isAutoConfirmEnabled,
    enclaveSessionValidUntil,
    isBiometricAuthEnabled,
    hasPasscodeAuth: global.authTypes?.includes('passcode'),
    shouldMigrate,
    hasLegacyBiometrics,
    legacyAuthConfig,
    authUsageCountRequest: selectAuthUsageCountRequest(global),
  };
})(PasswordForm));
